import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { apiRequest, pageQuery, type Envelope, type PagedData } from "../api/client";
import { readClaims } from "../auth/token";
import { parseBusinessHours, taskContactLog } from "./task-logic";

// TASK ENDPOINTS (`src/app/_pages/task/task.service.ts`). BODIES ARE THE ANGULAR
// ONES; DATES SERIALIZE AS ISO UTC STRINGS, AS ANGULAR'S DEFAULT JSON DOES.

export interface TaskPlayer {
  taskId?: number | null;
  playerId: number;
  playerName?: string;
  cardId?: string;
  cmsPlayerId?: string;
  birthdate?: string;
  completed?: boolean;
  taskOutcomeId?: number | null;
  taskOutcomeNote?: string | null;
}

// THE TASK AS THE SERVER RETURNS IT. UPDATE SENDS BACK WHATEVER CAME IN, PLUS EDITS,
// SO UNKNOWN SERVER FIELDS ARE CARRIED THROUGH RATHER THAN DROPPED.
export interface Task {
  id?: number | null;
  userId?: number | null;
  startDay?: string | Date | null;
  startTime: string | Date;
  endTime: string | Date;
  title: string;
  description?: string | null;
  managerUserId?: number | null;
  completed: boolean;
  expirationDate?: string | Date | null;
  parentTaskId?: number | null;
  alertTime?: string | Date | null;
  alertOption?: string | null;
  taskActionId?: number | null;
  taskActionName?: string;
  players: TaskPlayer[];
  [serverField: string]: unknown;
}

export interface Lookup {
  id: number;
  name: string;
  code: string;
}

const key = (...parts: unknown[]) => [String(readClaims()?.userId ?? "anonymous"), "tasks", ...parts];

// LOOKUPS ----------------------------------------------------------------------

const taskActionsQuery = () => ({
  queryKey: key("actions"),
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    apiRequest<Envelope<Lookup[] | null>>("/crm/task/get-task-actions", { signal }).then((r) => r.data ?? []),
  staleTime: Infinity,
});

export const useTaskActions = () => useQuery(taskActionsQuery());

export function useTaskOutcomes() {
  return useQuery({
    queryKey: key("outcomes"),
    queryFn: ({ signal }) => apiRequest<Envelope<Lookup[] | null>>("/crm/task/get-task-outcomes", { signal }).then((r) => r.data ?? []),
    staleTime: Infinity,
  });
}

// THE SIGNED-IN USER, FOR BUSINESS HOURS (`POST /user/get-user` WITH AN EMPTY BODY)
export function useBusinessHours() {
  return useQuery({
    queryKey: key("user"),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<{ businessHours?: string | null } | null>>("/user/get-user", { method: "POST", body: {}, signal }).then((r) => r.data),
    staleTime: Infinity,
    select: (user) => parseBusinessHours(user?.businessHours),
  });
}

// "RELATE PLAYERS" SEARCH: A NAME PREFIX MATCH OVER `/ht/paged-players`, 1000 ROWS,
// `condition: "OR"` - VERBATIM FROM `searchPlayers` IN BOTH TASK EDITORS
export interface PlayerSuggestion {
  id: number;
  name: string;
  cardId?: string;
  birthdate?: string;
}

export function searchPlayers(query: string, signal?: AbortSignal) {
  return apiRequest<Envelope<PagedData<PlayerSuggestion>>>(
    `/ht/paged-players?${pageQuery({ page: 1, pageSize: 1000, orderBy: "name", orderDir: "asc" })}`,
    { method: "POST", body: [{ field: "name", value: `${query}%`, valueDataType: "string", type: "ILIKE", condition: "OR" }], signal },
  ).then((r) => r.data?.rows ?? []);
}

// HOST TASKS -------------------------------------------------------------------

export function useTasksInRange(start: Date | null, end: Date | null) {
  return useQuery({
    queryKey: key("range", start?.toISOString(), end?.toISOString()),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<Task[] | null>>("/crm/task/get-tasks", { method: "POST", body: { start_time: start, end_time: end }, signal }).then(
        (r) => r.data ?? [],
      ),
    enabled: start !== null && end !== null,
    placeholderData: (previous) => previous,
  });
}

// EVERY TASK WRITE INVALIDATES EVERY TASK LIST, SO THE CALENDAR AND DAILY TASKS
// CAN NEVER SHOW A STALE COPY OF A TASK THE OTHER ONE JUST CHANGED
export const invalidateTasks = (qc: QueryClient) => qc.invalidateQueries({ queryKey: key() });

function useTaskWrite<TBody, TResult>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => apiRequest<Envelope<TResult>>(path, { method: "POST", body }).then((r) => r.data),
    onSettled: () => invalidateTasks(qc),
  });
}

export const useCreateTask = () => useTaskWrite<Task, { id: number }>("/crm/task/create-task");
export const useUpdateTask = () => useTaskWrite<Task, unknown>("/crm/task/update-task");
export const useDeleteTask = () => useTaskWrite<Task, unknown>("/crm/task/delete-task");

// COMPLETE ONE PLAYER ON A TASK, THEN - ONLY ON A FALSE-TO-TRUE COMPLETION - LOG
// THE CONTACT ON THE PLAYER'S TIMELINE. PORT OF `task-player-complete.save()`.
export interface CompletePlayerInput {
  player: TaskPlayer;
  taskId: number;
  // THE ACTION THE TASK WAS SAVED WITH, NOT WHATEVER IS IN THE EDITOR'S DROPDOWN NOW
  persistedTaskActionId: number | null;
  wasCompleted: boolean;
}

export function useCompleteTaskPlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ player, taskId, persistedTaskActionId, wasCompleted }: CompletePlayerInput) => {
      await apiRequest("/crm/task/update-task-player-relation", {
        method: "POST",
        body: {
          playerId: player.playerId,
          taskId,
          completed: player.completed ?? false,
          taskOutcomeId: player.taskOutcomeId ?? null,
          taskOutcomeNote: player.taskOutcomeNote ?? null,
        },
      });

      // THE RELATION IS SAVED. A FAILED LOG BELOW MUST NOT UNDO THAT, SO IT IS
      // REPORTED SEPARATELY RATHER THAN THROWN.
      if (!player.completed || wasCompleted || !persistedTaskActionId) return { logFailed: false };
      try {
        // WAIT FOR THE ACTION CODES RATHER THAN READ A HALF-LOADED LIST
        const actions = await qc.fetchQuery(taskActionsQuery());
        const body = taskContactLog({
          playerId: player.playerId,
          taskId,
          actionCode: actions.find((a) => a.id === persistedTaskActionId)?.code,
          note: player.taskOutcomeNote,
        });
        if (body) await apiRequest("/player/player-communication", { method: "POST", body });
        return { logFailed: false };
      } catch {
        return { logFailed: true };
      }
    },
    onSettled: () => invalidateTasks(qc),
  });
}

// MANAGER --------------------------------------------------------------------

// ONE GROUP PER HOST, IN THE SERVER'S ORDER (THE ANGULAR MANAGER VIEW DOES NOT SORT)
export interface HostTasks {
  hostName: string;
  taskDtos?: Task[] | null;
}

export function useAllHostTasks(start: Date, end: Date) {
  return useQuery({
    queryKey: key("all-hosts", start.toISOString(), end.toISOString()),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<HostTasks[] | null>>("/crm/task/get-all-host-tasks", { method: "POST", body: { start_time: start, end_time: end }, signal }).then(
        (r) => r.data ?? [],
      ),
    placeholderData: (previous) => previous,
  });
}

export function fetchTask(id: number, signal?: AbortSignal) {
  return apiRequest<Envelope<Task | null>>(`/crm/task/get-task/${id}`, { signal }).then((r) => {
    if (!r.data) throw new Error("The task could not be loaded.");
    return r.data;
  });
}

// A DAY AS THE TASK ENDPOINTS WANT IT: LOCAL MIDNIGHT TO THE NEXT LOCAL MIDNIGHT
export function dayRange(day: Date): { start: Date; end: Date } {
  const start = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  return { start, end: new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1) };
}
